import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { TrendingUp, Gift, Users, DollarSign, CheckCircle, MessageSquare } from 'lucide-react';
import { partnerAPI, heroAPI } from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PartnerProgramPage = () => {
  const [partnerProgram, setPartnerProgram] = useState(null);
  const [heroContent, setHeroContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [partnerRes, heroRes] = await Promise.all([
          partnerAPI.get(),
          heroAPI.get()
        ]);

        setPartnerProgram(partnerRes.data);
        setHeroContent(heroRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="public-page min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Загружается...</div>
      </div>
    );
  }

  return (
    <div className="public-page min-h-screen bg-gray-900 text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
              {partnerProgram?.title || "Партнёрская программа"}
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              {partnerProgram?.description || "Зарабатывайте с нашим AI-переводчиком. Двухуровневая партнёрка с высокими комиссиями"}
            </p>
            <div className="flex justify-center space-x-4">
              <Badge className="bg-green-600 text-white px-4 py-2 text-lg">
                {partnerProgram?.level1_percent || 8}% с первого уровня
              </Badge>
              <Badge className="bg-purple-600 text-white px-4 py-2 text-lg">
                {partnerProgram?.level2_percent || 20}% со второго уровня
              </Badge>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Commission Levels */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="bg-gradient-to-br from-green-900/30 to-gray-800 border-green-700 h-full">
                <CardHeader>
                  <CardTitle className="text-3xl text-green-400 flex items-center">
                    <TrendingUp className="mr-3 h-8 w-8" />
                    1-й уровень
                  </CardTitle>
                  <CardDescription className="text-gray-300 text-lg">
                    Прямые рефералы
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-6xl font-bold text-green-400">
                    {partnerProgram?.level1_percent || 8}%
                  </div>
                  <p className="text-gray-300 text-lg leading-relaxed">
                    {partnerProgram?.level1_description || "8% с покупки пакетов сообщений, приглашённых по ссылке вами!"}
                  </p>
                  <div className="bg-green-900/20 rounded-lg p-4">
                    <h4 className="font-semibold text-green-400 mb-2">Пример расчёта:</h4>
                    <div className="space-y-1 text-sm text-gray-300">
                      <div>Реферал купил пакет за 1000₽</div>
                      <div>Ваша комиссия: 80₽</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Card className="bg-gradient-to-br from-purple-900/30 to-gray-800 border-purple-700 h-full">
                <CardHeader>
                  <CardTitle className="text-3xl text-purple-400 flex items-center">
                    <Gift className="mr-3 h-8 w-8" />
                    2-й уровень
                  </CardTitle>
                  <CardDescription className="text-gray-300 text-lg">
                    Рефералы ваших рефералов
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-6xl font-bold text-purple-400">
                    {partnerProgram?.level2_percent || 20}%
                  </div>
                  <p className="text-gray-300 text-lg leading-relaxed">
                    {partnerProgram?.level2_description || "20% от дохода партнёров, которых пригласили они"}
                  </p>
                  <div className="bg-purple-900/20 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-400 mb-2">Пример расчёта:</h4>
                    <div className="space-y-1 text-sm text-gray-300">
                      <div>Ваш реферал заработал 100₽</div>
                      <div>Ваша комиссия: 20₽</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it works */}
      {partnerProgram?.how_it_works && partnerProgram.how_it_works.length > 0 && (
        <section className="py-20 bg-gray-800/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">Как это работает</h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Простые шаги для начала заработка в партнёрской программе
              </p>
            </motion.div>

            <div className="max-w-4xl mx-auto">
              {partnerProgram.how_it_works.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-center space-x-6 mb-8"
                >
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <Card className="bg-gray-800 border-gray-700">
                      <CardContent className="p-6">
                        <p className="text-lg text-gray-300">{step}</p>
                      </CardContent>
                    </Card>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Преимущества программы</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Почему стоит стать нашим партнёром
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: DollarSign,
                title: "Высокие комиссии",
                description: "До 20% от доходов партнёров второго уровня"
              },
              {
                icon: TrendingUp,
                title: "Растущий рынок",
                description: "AI-переводы становятся всё более популярными"
              },
              {
                icon: Users,
                title: "Качественный продукт",
                description: "Клиенты довольны сервисом и возвращаются"
              },
              {
                icon: CheckCircle,
                title: "Прозрачность",
                description: "Полная статистика доходов в личном кабинете"
              },
              {
                icon: Gift,
                title: "Бонусы",
                description: "Дополнительные бонусы за активность"
              },
              {
                icon: MessageSquare,
                title: "Поддержка",
                description: "Помощь в продвижении и маркетинге"
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="bg-gray-800 border-gray-700 h-full text-center hover:bg-gray-750 transition-colors">
                  <CardHeader>
                    <div className="w-16 h-16 bg-blue-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <benefit.icon className="h-8 w-8 text-blue-400" />
                    </div>
                    <CardTitle className="text-white">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400">{benefit.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-900/20 via-gray-900 to-purple-900/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Начните зарабатывать уже сегодня</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Присоединяйтесь к партнёрской программе AI-переводчика и получайте стабильный доход от рефералов
            </p>
            <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-green-600 to-purple-600 hover:from-green-700 hover:to-purple-700 text-lg px-8 py-4"
                onClick={() => window.open(heroContent?.bot_link || "https://t.me/lngaua_bot", "_blank")}
                data-testid="become-partner-btn"
              >
                <Users className="mr-2 h-5 w-5" />
                Стать партнёром
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="border-gray-600 text-gray-300 hover:bg-gray-800 text-lg px-8 py-4"
                onClick={() => window.open(heroContent?.bot_link || "https://t.me/lngaua_bot", "_blank")}
              >
                <MessageSquare className="mr-2 h-5 w-5" />
                Попробовать бота
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PartnerProgramPage;
